// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Lab Report Page
// Generates a structured, print-ready academic lab report
// ─────────────────────────────────────────────────────────────────────────────
import { Printer, Download } from 'lucide-react';
import { Card, CardBody, EmptyState, StatRow } from '@/components/ui/index';
import { Button } from '@/components/ui/Button';
import { fmt, fmtShort, fmtPercent, fmtDate, fmtDateTime, downloadCSV } from '@/lib/utils';
import type { LCCAParameters, LCCAResults } from '@lcca-nexus/shared';
import { FileText } from 'lucide-react';

interface LabReportProps {
  project: {
    id: string;
    name: string;
    description?: string | null;
    courseSection?: string | null;
    instructor?: string | null;
    studentName?: string | null;
    studentId?: string | null;
    assetType?: string | null;
    createdAt: string;
    scenarios: unknown[];
  };
  results: LCCAResults | null;
  params:  LCCAParameters | null;
}

export function LabReport({ project, results, params }: LabReportProps) {

  if (!results || !params) {
    return (
      <EmptyState
        icon={<FileText size={28} />}
        title="No results to report"
        description="Run a calculation first, then return here to generate the printable lab report."
      />
    );
  }

  const now = new Date().toISOString();
  const analysisYears = params.analysisPeriod;
  const discountRate  = params.discountRate;

  // Cost rows for tables
  const costRows = [
    { label: 'Initial Capital Cost', undis: params.initialCost,              pw: results.initialCostPW  },
    { label: 'Operation & Maintenance Costs', undis: results.omUndiscounted, pw: results.omPW           },
    { label: 'Energy Costs',         undis: results.energyUndiscounted,      pw: results.energyPW       },
    { label: 'Replacement Costs',    undis: results.replacementUndiscounted, pw: results.replacementPW  },
    { label: 'Residual Value (−)',   undis: -params.residualValue,           pw: -results.residualPW    },
  ];

  const handleExportCSV = () => {
    const rows: string[][] = [
      ['LCCA LAB REPORT', project.name],
      ['Student', `${project.studentName ?? ''} (${project.studentId ?? ''})`],
      ['Course Section', project.courseSection ?? ''],
      ['Instructor', project.instructor ?? ''],
      ['Generated', fmtDateTime(now)],
      [],
      ['INPUT PARAMETERS'],
      ['Analysis Period', `${params.analysisPeriod} years`],
      ['Discount Rate', `${params.discountRate}%`],
      ['Initial Capital Cost', String(params.initialCost)],
      ['Residual Value', String(params.residualValue)],
      ['Annual O&M Cost', String(params.annualOM)],
      ['O&M Escalation Rate', `${params.omEscalation}%`],
      ['Annual Energy Cost', String(params.annualEnergy)],
      ['Energy Escalation Rate', `${params.energyEscalation}%`],
      [],
      ['REPLACEMENT EVENTS'],
      ['Description', 'Cost ($)', 'Year'],
      ...params.replacements.map(r => [r.label, String(r.cost), String(r.year)]),
      [],
      ['LCCA RESULTS SUMMARY'],
      ['Category', 'Undiscounted ($)', 'Present Worth ($)', '% of LCC'],
      ...costRows.map(r => [r.label, r.undis.toFixed(2), r.pw.toFixed(2), fmtPercent(r.pw / results.totalLCC * 100)]),
      ['TOTAL LCC (NPV)', '', results.totalLCC.toFixed(2), '100%'],
      [],
      ['KEY METRICS'],
      ['Total LCC (NPV)', results.totalLCC.toFixed(2)],
      ['Uniform Annual Cost (UAC)', results.uac.toFixed(2)],
      ['Residual Value (PW)', results.residualPW.toFixed(2)],
      [],
      ['YEAR-BY-YEAR CASH FLOWS'],
      ['Year', 'O&M ($)', 'Energy ($)', 'Replacement ($)', 'Total ($)', 'Discount Factor', 'Present Worth ($)', 'Cumulative PW ($)'],
      ...results.cashFlows.map(y => [
        String(y.year), y.omCost.toFixed(2), y.energyCost.toFixed(2),
        y.replacementCost.toFixed(2), y.totalCost.toFixed(2),
        y.discountFactor.toFixed(4), y.presentWorth.toFixed(2), y.cumulativePW.toFixed(2),
      ]),
    ];
    downloadCSV(`LCCA_Report_${project.name.replace(/\s+/g,'_')}.csv`, rows);
  };

  return (
    <div className="flex flex-col gap-4">

      {/* Toolbar */}
      <div className="no-print flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-navy-900 text-lg">Lab Report Preview</h3>
          <p className="text-xs text-navy-500">Print this page or export as CSV for your submission</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={<Download size={14} />} onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button variant="gold" icon={<Printer size={14} />} onClick={() => window.print()}>
            Print / Save PDF
          </Button>
        </div>
      </div>

      {/* ── REPORT BODY ── */}
      <Card className="overflow-hidden">
        {/* Cover header */}
        <div className="bg-navy-900 px-8 py-10 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center font-display font-bold text-sm">LC</div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-navy-300">LCCA Nexus · Lab Report</p>
                <p className="text-xs text-navy-400">Smart Technologies for Facility Management</p>
              </div>
            </div>
            <h1 className="font-display font-bold text-3xl text-white mb-2 leading-tight">
              Life-Cycle Cost Analysis
            </h1>
            <p className="text-navy-300 text-lg font-medium mb-6">{project.name}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Student',         value: project.studentName ?? '—' },
                { label: 'Student ID',      value: project.studentId ?? '—' },
                { label: 'Course Section',  value: project.courseSection ?? '—' },
                { label: 'Instructor',      value: project.instructor ?? '—' },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-navy-400">{f.label}</p>
                  <p className="text-white font-semibold text-sm mt-0.5">{f.value}</p>
                </div>
              ))}
            </div>
            <p className="text-navy-500 text-xs mt-6">Generated: {fmtDateTime(now)}</p>
          </div>
        </div>

        <CardBody className="p-8">

          {/* Section 1: Objective */}
          <Section num="1" title="Objective">
            <p className="text-sm text-navy-700 leading-relaxed">
              This report presents a Life-Cycle Cost Analysis (LCCA) for <strong>{project.name}</strong>.
              The objective is to determine the total present-worth cost of owning, operating, maintaining,
              and eventually disposing of the asset over a <strong>{analysisYears}-year</strong> study period,
              using a discount rate of <strong>{discountRate}%</strong> per annum.
            </p>
            {project.description && (
              <p className="text-sm text-navy-600 leading-relaxed mt-3 italic border-l-2 border-amber-400 pl-4">
                {project.description}
              </p>
            )}
          </Section>

          {/* Section 2: Methodology */}
          <Section num="2" title="Methodology">
            <p className="text-sm text-navy-700 leading-relaxed mb-3">
              LCCA was performed following the guidelines of <strong>ISO 15686-5:2017</strong>,{' '}
              <strong>ASTM E917</strong>, and <strong>NIST Handbook 135</strong>. All future costs
              are discounted to present value using the single present worth (SPW) and uniform
              present worth (UPW) factors. The total LCC is calculated as:
            </p>
            <div className="bg-navy-900 rounded-lg px-5 py-4 font-mono text-amber-300 text-sm leading-relaxed">
              LCC = IC + PW(O&M) + PW(Energy) + PW(Replacements) − PW(Residual Value)
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
              {[
                { name: 'Discount Factor',       eq: 'DF(t) = (1 + d)⁻ᵗ' },
                { name: 'Escalating Annual PW',  eq: 'PW = Σ Cₜ(1+e)ᵗ(1+d)⁻ᵗ' },
                { name: 'Single PW Factor',      eq: 'SPW = (1+d)⁻ᵗ' },
                { name: 'Uniform PW Factor',     eq: 'UPW = [1−(1+d)⁻ᴺ] ÷ d' },
                { name: 'Capital Recovery',      eq: 'CRF = d ÷ [1−(1+d)⁻ᴺ]' },
                { name: 'Uniform Annual Cost',   eq: 'UAC = LCC × CRF' },
              ].map(f => (
                <div key={f.name} className="bg-parchment-50 rounded-lg p-3 border border-parchment-200">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-navy-400 mb-1">{f.name}</p>
                  <p className="font-mono text-navy-700 text-xs">{f.eq}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Section 3: Input Parameters */}
          <Section num="3" title="Input Parameters">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-display font-semibold uppercase tracking-widest text-navy-400 mb-2">Study Parameters</p>
                <div className="bg-parchment-50 rounded-lg p-4 border border-parchment-200">
                  <StatRow label="Analysis Period"    value={`${params.analysisPeriod} years`}    mono={false} />
                  <StatRow label="Discount Rate"      value={`${params.discountRate}% per annum`} mono={false} />
                  <StatRow label="Initial Cost"       value={fmt(params.initialCost)} />
                  <StatRow label="Residual Value"     value={fmt(params.residualValue)} />
                </div>
              </div>
              <div>
                <p className="text-xs font-display font-semibold uppercase tracking-widest text-navy-400 mb-2">Annual Costs</p>
                <div className="bg-parchment-50 rounded-lg p-4 border border-parchment-200">
                  <StatRow label="Annual O&M Cost"       value={fmt(params.annualOM)} />
                  <StatRow label="O&M Escalation"        value={`${params.omEscalation}% / year`} mono={false} />
                  <StatRow label="Annual Energy Cost"    value={fmt(params.annualEnergy)} />
                  <StatRow label="Energy Escalation"     value={`${params.energyEscalation}% / year`} mono={false} />
                </div>
              </div>
            </div>

            {params.replacements.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-display font-semibold uppercase tracking-widest text-navy-400 mb-2">
                  Planned Replacement Events
                </p>
                <div className="overflow-x-auto rounded-lg border border-parchment-200">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="!text-left">#</th>
                        <th className="!text-left">Description</th>
                        <th>Year</th>
                        <th>Cost ($)</th>
                        <th>Present Worth ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {params.replacements.map((r, i) => {
                        const df = Math.pow(1 + params.discountRate / 100, -r.year);
                        return (
                          <tr key={r.id}>
                            <td className="!font-mono !text-navy-500">{i + 1}</td>
                            <td className="!font-body">{r.label}</td>
                            <td>{r.year}</td>
                            <td>{fmt(r.cost)}</td>
                            <td>{fmt(r.cost * df)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Section>

          {/* Section 4: Results */}
          <Section num="4" title="Results & Analysis">

            {/* KPI summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total LCC (NPV)',  value: fmtShort(results.totalLCC), accent: 'bg-navy-700' },
                { label: 'Uniform Annual Cost', value: fmtShort(results.uac), accent: 'bg-amber-600' },
                { label: 'Residual PW',      value: fmtShort(results.residualPW), accent: 'bg-teal-600' },
                { label: 'Avg Cost/Year',    value: fmtShort(results.totalLCC / params.analysisPeriod), accent: 'bg-navy-400' },
              ].map(k => (
                <div key={k.label} className="bg-parchment-50 rounded-xl border border-parchment-200 overflow-hidden">
                  <div className={`h-1 ${k.accent}`} />
                  <div className="p-4">
                    <p className="text-[10px] font-display font-semibold uppercase tracking-widest text-navy-400 mb-1">{k.label}</p>
                    <p className="font-mono font-semibold text-navy-900 text-xl">{k.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Cost summary table */}
            <p className="text-xs font-display font-semibold uppercase tracking-widest text-navy-400 mb-2">
              Table 4.1 — LCCA Cost Summary
            </p>
            <div className="overflow-x-auto rounded-lg border border-parchment-200 mb-6">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="!text-left">Cost Category</th>
                    <th>Undiscounted ($)</th>
                    <th>Present Worth ($)</th>
                    <th>% of Total LCC</th>
                  </tr>
                </thead>
                <tbody>
                  {costRows.map(row => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{fmt(row.undis)}</td>
                      <td className={row.pw < 0 ? 'text-teal-700' : ''}>{fmt(row.pw)}</td>
                      <td className={row.pw < 0 ? 'text-teal-700' : ''}>{fmtPercent(row.pw / results.totalLCC * 100)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td>TOTAL LCC — Net Present Value</td>
                    <td>{fmt(costRows.reduce((a, r) => a + r.undis, 0))}</td>
                    <td>{fmt(results.totalLCC)}</td>
                    <td>100.0%</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Year-by-year table (first 15 rows) */}
            <p className="text-xs font-display font-semibold uppercase tracking-widest text-navy-400 mb-2">
              Table 4.2 — Year-by-Year Cash Flow (first 15 years shown)
            </p>
            <div className="overflow-x-auto rounded-lg border border-parchment-200">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th className="!text-left">Year</th>
                    <th>O&M ($)</th>
                    <th>Energy ($)</th>
                    <th>Replacement ($)</th>
                    <th>Total ($)</th>
                    <th>DF</th>
                    <th>PW ($)</th>
                    <th>Cumulative PW ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {results.cashFlows.slice(0, 15).map(y => (
                    <tr key={y.year} className={y.replacementCost > 0 ? 'bg-red-50/60' : ''}>
                      <td className="!font-mono">{y.year}</td>
                      <td>{fmt(y.omCost)}</td>
                      <td>{fmt(y.energyCost)}</td>
                      <td className={y.replacementCost > 0 ? '!font-bold !text-red-700' : ''}>
                        {y.replacementCost > 0 ? fmt(y.replacementCost) : '—'}
                      </td>
                      <td className="!font-semibold">{fmt(y.totalCost)}</td>
                      <td className="!text-navy-500">{y.discountFactor.toFixed(4)}</td>
                      <td>{fmt(y.presentWorth)}</td>
                      <td className="!font-semibold">{fmt(y.cumulativePW)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {results.cashFlows.length > 15 && (
              <p className="text-xs text-navy-400 mt-2 italic">
                * Full {results.cashFlows.length}-year table available in the exported CSV.
              </p>
            )}
          </Section>

          {/* Section 5: Sensitivity Analysis */}
          <Section num="5" title="Sensitivity Analysis">
            <p className="text-sm text-navy-700 mb-4 leading-relaxed">
              A sensitivity analysis was performed to assess the impact of a ±10% variation
              in key input parameters on the Total LCC. This identifies which parameters
              have the greatest influence on the cost outcome.
            </p>
            <div className="overflow-x-auto rounded-lg border border-parchment-200">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="!text-left">Parameter</th>
                    <th>Change</th>
                    <th>New LCC ($)</th>
                    <th>Δ LCC ($)</th>
                    <th>Δ LCC (%)</th>
                    <th className="!text-left">Impact Level</th>
                  </tr>
                </thead>
                <tbody>
                  {results.sensitivity.map((s, i) => {
                    const isUp  = s.deltaPercent > 0;
                    const abs   = Math.abs(s.deltaPercent);
                    const level = abs > 5 ? 'High' : abs > 2 ? 'Medium' : 'Low';
                    const lvlColor = abs > 5 ? 'text-red-700 bg-red-100' : abs > 2 ? 'text-amber-700 bg-amber-100' : 'text-teal-700 bg-teal-100';
                    return (
                      <tr key={i}>
                        <td>{s.label}</td>
                        <td>
                          <span className={`badge ${s.perturbation === '+10%' ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'}`}>
                            {s.perturbation}
                          </span>
                        </td>
                        <td>{fmt(s.newLCC)}</td>
                        <td className={isUp ? 'text-red-600 font-semibold' : 'text-teal-700 font-semibold'}>
                          {isUp ? '+' : ''}{fmt(s.deltaLCC)}
                        </td>
                        <td className={isUp ? 'text-red-600 font-semibold' : 'text-teal-700 font-semibold'}>
                          {isUp ? '+' : ''}{s.deltaPercent.toFixed(2)}%
                        </td>
                        <td>
                          <span className={`badge ${lvlColor}`}>{level}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Section 6: Conclusion */}
          <Section num="6" title="Conclusion">
            <p className="text-sm text-navy-700 leading-relaxed">
              The Life-Cycle Cost Analysis for <strong>{project.name}</strong> yields a
              Total LCC (Net Present Value) of <strong>{fmt(results.totalLCC)}</strong> over
              a <strong>{params.analysisPeriod}-year</strong> study period at a discount rate
              of <strong>{params.discountRate}%</strong>. The equivalent Uniform Annual Cost
              (UAC) is <strong>{fmt(results.uac)}</strong> per year.
            </p>
            <p className="text-sm text-navy-700 leading-relaxed mt-3">
              The dominant cost category is{' '}
              <strong>
                {costRows
                  .filter(r => r.pw > 0)
                  .sort((a, b) => b.pw - a.pw)[0]?.label ?? 'Initial Cost'}
              </strong>{' '}
              accounting for{' '}
              <strong>
                {fmtPercent(Math.max(
                  ...costRows.filter(r => r.pw > 0).map(r => r.pw / results.totalLCC * 100)
                ))}
              </strong>{' '}
              of the total LCC. The sensitivity analysis indicates that the Total LCC is most
              sensitive to changes in the{' '}
              <strong>
                {results.sensitivity.sort((a, b) => Math.abs(b.deltaPercent) - Math.abs(a.deltaPercent))[0]?.label ?? 'discount rate'}
              </strong>.
            </p>
          </Section>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-parchment-200 flex items-center justify-between text-xs text-navy-400">
            <span>LCCA Nexus · Smart Technologies for Facility Management · Bachelor of Civil Engineering</span>
            <span>{fmtDate(now)}</span>
          </div>

        </CardBody>
      </Card>
    </div>
  );
}

// ── Section helper ────────────────────────────────────────────────────────────
function Section({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-7 h-7 rounded-lg bg-navy-900 flex items-center justify-center text-white font-display font-bold text-xs flex-shrink-0">
          {num}
        </div>
        <h2 className="font-display font-bold text-navy-900 text-lg">{title}</h2>
        <div className="flex-1 h-px bg-parchment-200" />
      </div>
      {children}
    </div>
  );
}
